import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'ui';

import './exit-practice-dialog.scss';

/**
 * ExitPracticeDialog - Confirmation shown when a student leaves an in-progress practice
 * they created (TANAI-1994). Leans on the ui Dialog for the header (title + close X) and
 * the footer: the ghost "cancel" button discards the practice (`onDiscard`), the primary
 * "success" button saves it for later (`onSaveForLater`), and the X (`onClose`) just
 * dismisses the dialog and stays in the practice. This component only lays out the
 * horizontal body (mascot + text); it stays pure JSX with all logic in usePracticeExitFlow.
 *
 * The mascot is the StudyBuddy "help" variant, from the `--tangerine-ai-bot-help` CSS var
 * (defined per tenant in `public/css/styles.css`), so each tenant swaps it automatically.
 */

type ExitPracticeDialogProps = {
  isDiscarding?: boolean;
  isOpen: boolean;
  onClose: () => void;
  onDiscard: () => void;
  onSaveForLater: () => void;
};

function ExitPracticeDialog({ isDiscarding = false, isOpen, onClose, onDiscard, onSaveForLater }: Readonly<ExitPracticeDialogProps>): JSX.Element {
  const { t } = useTranslation(['chatbot']);

  return (
    <Dialog
      buttonTextCancel={t('practice_exit_discard_cta')}
      buttonTextSuccess={t('practice_exit_save_cta')}
      className="exit-practice-dialog"
      isAlert
      isLoading={isDiscarding}
      isOpen={isOpen}
      onCancel={onDiscard}
      onClose={onClose}
      onSuccess={onSaveForLater}
      size="m"
      title={t('practice_exit_dialog_title')}
    >
      <div className="exit-practice-dialog__content">
        <span aria-hidden="true" className="exit-practice-dialog__mascot" />
        <p className="exit-practice-dialog__body">{t('practice_exit_dialog_body')}</p>
      </div>
    </Dialog>
  );
}

export default ExitPracticeDialog;
